/**
 * audienceTokens — the single source of truth for the audience token vocabulary.
 *
 * This file is rendered live on the /brand page (Audience tab) AND imported by
 * the audience pickers, so an edit here changes both at once — the reference
 * page can never drift from what the app actually offers. Vault docs LINK to
 * /brand rather than copying these tables.
 *
 * Meanings are written to match what the SQL resolver actually does
 * (`audience_match` in supabase/migrations/20260624150000_phase_c_rls.sql, plus
 * the per-table `@parent-*` wrappers). The token vocabulary below is FINAL —
 * do not add tokens here without a resolver to back them.
 */

/**
 * The built-in audience tokens, in picker display order.
 * `tok` is the stored wire value: pseudo-groups carry the `@` prefix; `members`
 * and `admins` are the two role-slot space groups, stored as bare slugs.
 */
export const BUILTIN_AUDIENCES = [
  {
    tok: '@everyone',
    label: 'Everyone',
    meaning: 'Anyone, signed in or not — the only token a signed-out visitor can match.',
  },
  {
    tok: '@logged-in',
    label: 'Signed in',
    meaning: 'Any authenticated account. Anonymous sessions do not count — the resolver requires a claimed account, so today this admits exactly the same people as Claimed.',
  },
  {
    tok: '@verified',
    label: 'Verified',
    meaning: 'Accounts that verified their email.',
  },
  {
    tok: '@claimed',
    label: 'Claimed',
    meaning: 'Accounts that claimed their profile.',
  },
  {
    tok: 'members',
    label: 'Members',
    meaning: 'Active members of the space — the built-in members group.',
  },
  {
    tok: 'admins',
    label: 'Admins',
    meaning: 'Space admins — the built-in admins group.',
  },
  {
    tok: '@parent-viewers',
    label: 'Item viewers',
    meaning: 'Whoever can see the parent item — re-evaluated against the item’s own audience at read time.',
  },
  {
    tok: '@parent-editors',
    label: 'Item editors',
    meaning: 'Whoever can edit the parent item.',
  },
];

/**
 * Audiences that only mean something on an ANSWER (a cell), because they are worked out from the
 * answer itself: the person whose item it sits on, and the people a linked-item answer picks. They
 * are offered on the "who can see" axis of fields that store separate answers, and nowhere else —
 * an item has no "people it picks".
 *
 * Resolved by the database, not by audience_match: every write to such a field's cells stamps
 * `viewer_accounts` with those people's accounts (cells_stamp_named_people, migration
 * 20261109163418), read through group_members.profile_item_id. View only; never grants edit.
 */
export const ANSWER_PERSON_AUDIENCES = [
  {
    tok: '@this-person',
    label: 'This person',
    meaning: 'The person whose page the answer is on, once they have claimed their profile.',
  },
  {
    tok: '@picked-people',
    label: 'People it picks',
    meaning: 'Everyone a linked-item answer names, once they have claimed their profile.',
    linkedOnly: true,
  },
];

/**
 * The link counterpart: "Both people" — whoever each END of a relationship is, not whoever can see
 * them (that is `@both-endpoint-viewers`, which on a members-visible profile is every member).
 * Offered in a link's own privacy picker, and on the "who can see" axis of a field whose values are
 * links, where every new link under that field's relationship takes the field's audience.
 *
 * Resolved by the database the same way: every write to a link carrying it stamps the link's
 * `viewer_accounts` with the accounts behind its two ends (links_stamp_both_people, migration
 * 20261109163418). View only.
 */
export const LINK_PERSON_AUDIENCES = [
  {
    tok: '@both-people',
    label: 'Both people',
    meaning: 'The two people a relationship connects — whoever each end is, once they have claimed their profile.',
  },
];

const ANSWER_PERSON_TOKENS = new Set(ANSWER_PERSON_AUDIENCES.map((a) => a.tok));

/** @returns {boolean} whether a field shares each answer with the people the answer is about */
export const sharesWithAnswerPeople = (field) => [
  ...(Array.isArray(field?.field_view_groups) ? field.field_view_groups : []),
  ...(Array.isArray(field?.default_viewer_groups) ? field.default_viewer_groups : []),
].some((t) => ANSWER_PERSON_TOKENS.has(t));

/**
 * The open-ended token forms. Unlike the built-ins these carry an argument;
 * `example` is a representative token in the same shape, safe to feed to
 * `iconForToken` for a glyph.
 */
export const DYNAMIC_TOKEN_FORMS = [
  {
    form: 'acct:<account-id>',
    label: 'A named person',
    meaning: 'One specific account, granted by its account id.',
    example: 'acct:example',
  },
  {
    form: 'grp:<slug>',
    label: 'A named group',
    meaning: 'One space group, referenced by its slug.',
    example: 'grp:example',
  },
  {
    form: '@personal-group:<group-id>',
    label: 'A personal group',
    meaning: 'Everyone in one person’s own group, such as My Connections — view only, and only its owner can share their own notes, answers and items with it. Named by id because the group lives in its owner’s Personal Space while the row can be in any space.',
    example: '@personal-group:example',
  },
  {
    form: '@communities-of:<account-id>',
    label: 'All my communities',
    meaning: 'Everyone who shares a community with one person — active in the member, admin or owner group of any space they belong to, Personal Spaces aside. Resolved at read time, so it grows as they join communities and shrinks as they leave. View only, and only on a note, an answer or an item of their own. Named by account because the view checks see the audience, never its row.',
    example: '@communities-of:example',
  },
  {
    form: '@group:<group-id>',
    label: 'A group in another space',
    meaning: 'Everyone active in one group of another space, such as Core Team’s members — view only. Named by id because a bare slug like members only means something inside the row’s own space. Only a member of that group’s space can add it.',
    example: '@group:example',
  },
];

/**
 * Personal-group tokens, stored verbatim in `viewer_groups` (the `@` keeps them out of slug
 * matching). Resolved by `personal_group_match` in the view checks only — migration
 * 20260914001742 — so one of these never grants edit.
 */
export const PERSONAL_GROUP_PREFIX = '@personal-group:';

/** @returns {boolean} whether `token` names a personal group */
export const isPersonalGroupToken = (token) =>
  typeof token === 'string' && token.startsWith(PERSONAL_GROUP_PREFIX);

/**
 * Whether a group row is a Share with notes link (migration 20261118172304): a personal group whose
 * slug is `link-<item id>`, or `link-<item id>-revoked-<time>` once revoked. Read by slug so the check
 * works before and after the migration's columns exist. Link groups are chosen through Share with
 * notes, never offered in a group list or picker.
 */
export const isLinkGroupSlug = (slug) =>
  typeof slug === 'string' && /^link-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(-revoked-\d+)?$/i.test(slug);

/** @returns {string|null} the group id a personal-group token names */
export const personalGroupIdOf = (token) =>
  (isPersonalGroupToken(token) ? token.slice(PERSONAL_GROUP_PREFIX.length) : null);

/**
 * Space-group tokens: a group of ANOTHER space, named by id (`@group:<uuid>`). Resolved by
 * `group_token_match` in the view checks only — migration 20261011100000 — so one of these never
 * grants edit, and a group in a Personal Space never matches (that is `@personal-group:`).
 */
export const GROUP_TOKEN_PREFIX = '@group:';

/** @returns {boolean} whether `token` names a group of another space */
export const isGroupToken = (token) =>
  typeof token === 'string' && token.startsWith(GROUP_TOKEN_PREFIX);

/** @returns {string|null} the group id a space-group token names */
export const groupIdOf = (token) =>
  (isGroupToken(token) ? token.slice(GROUP_TOKEN_PREFIX.length) : null);

/**
 * "All my communities": everyone who shares a community with one account (`@communities-of:<uuid>`).
 * Resolved by `communities_of_match` in the view checks only — migration 20261109100000 — so one of
 * these never grants edit, and only the account it names may put it on their own rows.
 */
export const COMMUNITIES_OF_PREFIX = '@communities-of:';

/** @returns {boolean} whether `token` is an "All my communities" token */
export const isCommunitiesToken = (token) =>
  typeof token === 'string' && token.startsWith(COMMUNITIES_OF_PREFIX);

/** @returns {string|null} the account whose communities the token names */
export const communitiesOwnerOf = (token) =>
  (isCommunitiesToken(token) ? token.slice(COMMUNITIES_OF_PREFIX.length) : null);

/** @returns {string} the stored token for one account's communities */
export const communitiesToken = (accountId) => `${COMMUNITIES_OF_PREFIX}${accountId}`;

/** @returns {boolean} whether `token` names a group by id — a personal group or another space's */
export const isIdGroupToken = (token) => isPersonalGroupToken(token) || isGroupToken(token);

/** @returns {string} the stored token for a group id */
export const groupToken = (groupId) => `${GROUP_TOKEN_PREFIX}${groupId}`;

/**
 * The words for a space-group token, from a `describeSpaceGroups` entry. A member of the group's
 * space sees "Members · Core Team"; anyone else only that it is a group in another space, because
 * the RPC withholds names from non-members.
 */
export function spaceGroupLabel(desc) {
  if (!desc || !desc.name) return 'A group in another space';
  return desc.spaceName ? `${desc.name} · ${desc.spaceName}` : desc.name;
}

/**
 * The words for a personal group on a row, from a `describePersonalGroups` entry. Your own reads as
 * yours; someone else's is described by its owner, and a group's own name is never shown to anyone
 * but its owner (the RPC withholds it).
 */
export function personalGroupLabel(desc) {
  if (!desc) return 'A personal group';
  if (desc.isMine) return desc.isConnections ? 'My Connections' : (desc.name || 'My group');
  if (desc.isConnections) return `Connections of ${desc.ownerName || 'someone'}`;
  return `${desc.ownerName || 'Someone'}’s group`;
}

/**
 * What an EMPTY audience array means, per fields audience column. Empty is
 * never "no rule" — each axis has its own deliberate default, and these are
 * the words the UI uses for it.
 */
export const AXIS_EMPTY_MEANINGS = {
  field_view_groups: 'Same as the item',
  who_can_answer_groups: 'No one — machine-written',
  default_viewer_groups: 'Only each author (blind)',
  default_editor_groups: 'Only each author',
  aggregate_visibility_groups: 'Same as the answers',
};
